import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useUser, useAuth, UserProfile, useClerk } from '@clerk/clerk-react';

const UserSettingsContainer = styled.div`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 24px;
  padding: 2rem;
  box-shadow: 
    0 20px 40px rgba(0, 0, 0, 0.1),
    0 0 0 1px rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #9b59b6, #8e44ad, #e74c3c, #f39c12);
    border-radius: 24px 24px 0 0;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 2rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const BackButton = styled.button`
  background: rgba(0, 0, 0, 0.1);
  border: none;
  border-radius: 12px;
  padding: 0.75rem 1rem;
  color: #374151;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(0, 0, 0, 0.2);
    transform: translateY(-2px);
  }
`;

const Title = styled.h2`
  margin: 0;
  font-size: 2rem;
  font-weight: 700;
  background: linear-gradient(135deg, #9b59b6 0%, #8e44ad 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const SettingsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 2rem;
  margin-top: 2rem;
`;

const SettingsCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.8);
  border-radius: 16px;
  padding: 2rem;
  border: 1px solid rgba(0, 0, 0, 0.1);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
`;

const CardTitle = styled.h3`
  margin: 0 0 1rem 0;
  font-size: 1.2rem;
  font-weight: 600;
  color: #374151;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: #374151;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #9b59b6;
    box-shadow: 0 0 0 3px rgba(155, 89, 182, 0.1);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 1rem;
  background: white;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #9b59b6;
    box-shadow: 0 0 0 3px rgba(155, 89, 182, 0.1);
  }
`;

const Button = styled.button`
  background: linear-gradient(135deg, #9b59b6, #8e44ad);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.75rem 1.5rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(155, 89, 182, 0.3);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const InfoText = styled.p`
  margin: 0.5rem 0 0 0;
  font-size: 0.9rem;
  color: #6b7280;
`;

const ErrorMessage = styled.div`
  background: rgba(231, 76, 60, 0.1);
  border: 1px solid rgba(231, 76, 60, 0.2);
  border-radius: 8px;
  padding: 1rem;
  color: #e74c3c;
  margin-bottom: 1rem;
`;

const SuccessMessage = styled.div`
  background: rgba(46, 204, 113, 0.1);
  border: 1px solid rgba(46, 204, 113, 0.2);
  border-radius: 8px;
  padding: 1rem;
  color: #2ecc71;
  margin-bottom: 1rem;
`;

const ProfileImageSection = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const ProfileImage = styled.img`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid #9b59b6;
`;

const ImageUploadButton = styled.label`
  background: linear-gradient(135deg, #9b59b6, #8e44ad);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.5rem 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(155, 89, 182, 0.3);
  }
`;

const ClerkProfileContainer = styled.div`
  background: rgba(255, 255, 255, 0.8);
  border-radius: 16px;
  padding: 2rem;
  border: 1px solid rgba(0, 0, 0, 0.1);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  margin-top: 2rem;
`;

const UserSettings = ({ onBack }) => {
  const { user } = useUser();
  const { getToken } = useAuth();
  const clerk = useClerk();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showClerkProfile, setShowClerkProfile] = useState(false);
  
  // Form states
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');

  useEffect(() => {
    if (user) {
      setFirstName(user.firstName || '');
      setLastName(user.lastName || '');
      setEmail(user.emailAddresses[0]?.emailAddress || '');
    }
  }, [user]);

  const handleSaveProfile = async () => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      // Clerk-Profil direkt aktualisieren
      await user.update({
        firstName: firstName,
        lastName: lastName,
      });
      
      setSuccess('Profil erfolgreich aktualisiert!');
    } catch (err) {
      setError(err.message || 'Fehler beim Speichern des Profils');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      setLoading(true);
      setError(null);
      
      // Clerk-Profilbild aktualisieren
      await user.setProfileImage({ file });
      
      setSuccess('Profilbild erfolgreich aktualisiert!');
    } catch (err) {
      setError(err.message || 'Fehler beim Hochladen des Profilbilds');
    } finally {
      setLoading(false);
    }
  };

  return (
    <UserSettingsContainer>
      <Header>
        <HeaderLeft>
          <BackButton onClick={onBack}>
            ← Zurück zum Dashboard
          </BackButton>
          <Title>Nutzereinstellungen</Title>
        </HeaderLeft>
      </Header>

      {error && <ErrorMessage>{error}</ErrorMessage>}
      {success && <SuccessMessage>{success}</SuccessMessage>}

      <SettingsGrid>
        <SettingsCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
         >
           <CardTitle>👤 Profil-Informationen</CardTitle>
           
           <ProfileImageSection>
             <ProfileImage 
               src={user?.imageUrl || '/default-avatar.png'} 
               alt="Profilbild"
             />
             <div>
               <ImageUploadButton>
                 Profilbild ändern
                 <input
                   type="file"
                   accept="image/*"
                   onChange={handleImageUpload}
                   style={{ display: 'none' }}
                 />
               </ImageUploadButton>
             </div>
           </ProfileImageSection>
           
           <FormGroup>
            <Label>Vorname</Label>
            <Input
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="Ihr Vorname"
            />
          </FormGroup>
          
          <FormGroup>
            <Label>Nachname</Label>
            <Input
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="Ihr Nachname"
            />
          </FormGroup>
          
           <FormGroup>
             <Label>E-Mail-Adresse</Label>
             <Input
               type="email"
               value={email}
               disabled
               placeholder="ihre@email.de"
             />
             <InfoText>E-Mail-Adresse wird über Clerk verwaltet</InfoText>
           </FormGroup>
          
          <Button onClick={handleSaveProfile} disabled={loading}>
            {loading ? 'Speichern...' : 'Profil speichern'}
          </Button>
        </SettingsCard>

        <SettingsCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          <CardTitle>⚙️ Erweiterte Einstellungen</CardTitle>
          
          <Button 
            onClick={() => setShowClerkProfile(!showClerkProfile)}
            style={{ marginBottom: '1rem' }}
          >
            {showClerkProfile ? 'Clerk-Profil ausblenden' : 'Clerk-Profil anzeigen'}
          </Button>
          
          <InfoText>
            Für erweiterte Einstellungen wie Passwort, 2FA und E-Mail-Verwaltung 
            können Sie das vollständige Clerk-Profil verwenden.
          </InfoText>
        </SettingsCard>
      </SettingsGrid>

      {showClerkProfile && (
        <ClerkProfileContainer>
          <UserProfile />
        </ClerkProfileContainer>
      )}
    </UserSettingsContainer>
  );
};

export default UserSettings;
